import { Select } from "@/components/ui/Select";
import { FormControl } from "@/components/ui/FormControl";
import classes from "./PersonalDetails.module.scss";
import { Input } from "@/components/ui/Input";
import type { PersonalDetailsProps } from "./PersonalDetailsProps";
import { clsx } from "clsx";
import { useTranslation } from "@/hooks/useTranslation";
import { ValidationGroupProvider } from "@/contexts/ValidationGroupProvider";
import { Field } from "../Field";
import * as z from "zod";
import type { PersonalDetails as PersonalDetailsType } from "@/stores";

export function PersonalDetails(props: PersonalDetailsProps) {
  const { config, className, ref } = props;

  const t = useTranslation("personal_details");

  const titleOptions = config["personal-details-title-options"].map(
    (option) => ({
      value: option.value,
      label: t(option.i18n),
    }),
  );

  const {
    title,
    "first-name": firstName,
    "last-name": lastName,
    email,
  } = config["personal-details-fields"];

  return (
    <ValidationGroupProvider name="personalDetails" ref={ref}>
      <div className={clsx(classes.wrapper, className)}>
        <div className={classes.formGrid}>
          <Field<PersonalDetailsType>
            name="title"
            serverErrorPaths={["personal_details.title"]}
            validate={() => {
              if (title["is-required"]) {
                return z.string().min(1, t("errors.title-required") as string);
              }
              return null;
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t(title.label.i18n)}
                error={errorMessage}
                isValid={isValid}
              >
                <Select
                  ref={ref as React.RefObject<HTMLSelectElement>}
                  name="title"
                  value={value}
                  onChange={(ev) => handleChange(ev.target.value)}
                  onBlur={() => handleBlur()}
                >
                  <option value="">{t("title-placeholder")}</option>
                  {titleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Field<PersonalDetailsType>
            name="firstName"
            serverErrorPaths={["personal_details.first_name"]}
            validate={() => {
              if (firstName["is-required"]) {
                return z
                  .string()
                  .min(1, t("errors.first-name-required") as string);
              }
              return null;
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t(firstName.label.i18n)}
                error={errorMessage}
                isValid={isValid}
              >
                <Input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  name="firstName"
                  value={value}
                  onChange={(e) => handleChange(e.target.value, e)}
                  onBlur={() => handleBlur()}
                />
              </FormControl>
            )}
          />

          <Field<PersonalDetailsType>
            name="lastName"
            serverErrorPaths={["personal_details.last_name"]}
            validate={() => {
              if (lastName["is-required"]) {
                return z
                  .string()
                  .min(1, t("errors.last-name-required") as string);
              }
              return null;
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t(lastName.label.i18n)}
                error={errorMessage}
                isValid={isValid}
              >
                <Input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  name="lastName"
                  value={value}
                  onChange={(e) => {
                    handleChange(e.target.value, e);
                  }}
                  onBlur={() => handleBlur()}
                />
              </FormControl>
            )}
          />
        </div>

        <div className={classes.formGrid}>
          <Field<PersonalDetailsType>
            name="email"
            serverErrorPaths={["personal_details.email"]}
            validate={() => {
              if (email["is-required"]) {
                return z.string().email(t("errors.email-invalid") as string);
              }
              return z
                .string()
                .email(t("errors.email-invalid") as string)
                .optional();
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t(email.label.i18n)}
                error={errorMessage}
                isValid={isValid}
              >
                <Input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  type="email"
                  name="email"
                  value={value}
                  onChange={(e) => handleChange(e.target.value, e)}
                  onBlur={() => handleBlur()}
                />
              </FormControl>
            )}
          />

          <Field<PersonalDetailsType>
            name="confirmEmail"
            dependencies={["email"]}
            serverErrorPaths={["personal_details.confirm_email"]}
            validate={(groupStateValues) => {
              if (email["is-required"]) {
                return z
                  .string()
                  .email(t("errors.email-invalid") as string)
                  .refine(
                    (val) => val === groupStateValues.email,
                    t("errors.email-mismatch") as string,
                  );
              }

              return z
                .string()
                .email(t("errors.email-invalid") as string)
                .refine(
                  (val) => val === groupStateValues.email,
                  t("errors.email-mismatch") as string,
                )
                .optional();
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t("confirm-email")}
                error={errorMessage}
                isValid={isValid}
              >
                <Input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  type="email"
                  name="confirmEmail"
                  value={value}
                  onChange={(e) => handleChange(e.target.value, e)}
                  onBlur={() => handleBlur()}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
    </ValidationGroupProvider>
  );
}
